// Automation ideas — what the managers want automated, and the owner's view of
// all four clinics' lists at once.
//
// Owner, 2026-08-17. He first asked for a Google Keep button that would gather
// managers' notes and mail them to him. Keep cannot do that in any way worth
// trusting — it has no ordinary API, and a note living there carries no clinic,
// no author this system knows and no date it can group by. He was told that,
// and answered with the better idea:
//
//   "lets do this.. just use the Google Keep Logo but have it open a note entry
//    form that they can keep adding notes that then get logged and sent to me?"
//   "i need this not for general notes rather ideas they want me to automate"
//   "i want in the note a title and note"
//
// So the Keep mark in the nav opens THIS page, not keep.google.com. The logo is
// the doorway he recognises; everything behind it is ours.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useResource, refetchResource, peekResource, persistResource } from '../lib/store';
import { session } from '../lib/session';
import { authedGet, authedGetBlob, authedWrite, ApiError } from '../lib/apiClient';
import { FaultState, EmptyState } from '../components/FaultState';
import { useWrite } from '../components/MoneyForms';
import { uploadIdeaFile } from '../lib/ideaFiles';
import { LOCATION_NAMES, LOCATION_ORDER, type LocationCode } from '../lib/locations';
import { visibleClinics } from '../components/ClinicTabs';
import { formatDate } from '../lib/date';
import { clinicNow } from '../lib/clinicDay';

export interface IdeaAttachment {
  id: number;
  automation_idea_id: number;
  url: string;
  name: string | null;
  added_by: string;
  added_at: string;
}

export interface AutomationIdea {
  id: number;
  location_code: string;
  title: string;
  body: string;
  author: string;
  created_at: string;
  updated_at: string | null;
  updated_by: string | null;
  /** Owner, 2026-08-19: "everyone" may attach a document. Links, not files —
   * the app stores no bytes, the same rule task attachments follow. Defaulted
   * rather than asserted: an older API still deploying must not blank the
   * list. */
  attachments?: IdeaAttachment[];
  reply?: string | null;
  reply_at?: string | null;
  reply_by?: string | null;
  done_at?: string | null;
  done_note?: string | null;
  done_by?: string | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

interface IdeasData {
  ideas: AutomationIdea[];
  locations: string[];
}

const KEY = 'automation-ideas:v4';
const fetchIdeas = (): Promise<IdeasData> => authedGet<IdeasData>('/api/v1/automation-ideas');
const reload = (): Promise<void> => refetchResource<IdeasData>(KEY, fetchIdeas);

async function dropIdeaFromCache(id: number): Promise<void> {
  const current = peekResource<IdeasData>(KEY);
  if (!current) return;
  await persistResource(KEY, {
    ...current,
    ideas: (current.ideas ?? []).map((idea) => (
      idea.id === id ? { ...idea, deleted_at: idea.deleted_at || new Date().toISOString() } : idea
    )),
  });
}

/** The clinic day an idea was written on — never the reader's own timezone
 * (lib/clinicDay.ts). */
function isOpenIdea(idea: AutomationIdea): boolean {
  return !idea.done_at && !idea.deleted_at;
}

function completedOn(idea: AutomationIdea): string {
  return idea.done_at || idea.deleted_at || idea.updated_at || idea.created_at;
}

function writtenLine(idea: AutomationIdea): string {
  const d = new Date(idea.created_at);
  const when = Number.isNaN(d.getTime()) ? '' : `${formatDate(clinicNow(d))} — `;
  return `${when}${idea.author}${idea.updated_by ? ' — edited' : ''}`;
}

function canRespondToIdeas(): boolean {
  const role = session.value.me?.role;
  return role === 'OWNER' || role === 'DIRECTOR';
}

function IdeaRow({ idea }: { idea: AutomationIdea }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(idea.title);
  const [body, setBody] = useState(idea.body);
  const [confirming, setConfirming] = useState(false);
  const edit = useWrite();
  const del = useWrite();
  const canRespond = canRespondToIdeas();

  async function saveEdit(): Promise<void> {
    await edit.run(async () => {
      await authedWrite('PATCH', `/api/v1/automation-ideas/${idea.id}`, { title, body });
      await reload();
    }, () => setEditing(false));
  }

  async function confirmDelete(): Promise<void> {
    await del.run(async () => {
      await authedWrite('DELETE', `/api/v1/automation-ideas/${idea.id}`);
      await dropIdeaFromCache(idea.id);
      await reload();
    });
  }

  if (editing) {
    return (
      <li data-testid={`idea-${idea.id}`}>
        <input
          type="text" class="idea-title" aria-label={`Edit title ${idea.id}`}
          value={title}
          onInput={(ev: Event) => setTitle((ev.target as HTMLInputElement).value)}
        />
        <textarea
          class="notes-entry" rows={6} aria-label={`Edit idea ${idea.id}`}
          value={body}
          onInput={(ev: Event) => setBody((ev.target as HTMLTextAreaElement).value)}
        />
        <button
          type="button" class="btn primary" disabled={edit.busy || !title.trim() || !body.trim()}
          onClick={() => void saveEdit()}
        >
          {edit.busy ? 'Saving…' : 'Save'}
        </button>{' '}
        <button
          type="button" class="btn" disabled={edit.busy}
          onClick={() => { setTitle(idea.title); setBody(idea.body); setEditing(false); }}
        >
          Cancel
        </button>
        {edit.error ? <small class="bad" role="alert">{edit.error}</small> : null}
      </li>
    );
  }

  return (
    <li data-testid={`idea-${idea.id}`}>
      <div class="section-head-row">
        <div>
          <strong>{idea.title}</strong>
          <div class="empty">{writtenLine(idea)}</div>
        </div>
        <div class="section-head-actions">
          <button type="button" class="btn" onClick={() => setEditing(true)}>Edit</button>
          {/* Inline confirm, never `window.confirm` — it freezes the tab
              (docs/known-traps.md). */}
          {confirming ? (
            <span data-testid={`idea-${idea.id}-confirm`}>
              Delete?{' '}
              <button type="button" class="btn" disabled={del.busy} onClick={() => void confirmDelete()}>
                {del.busy ? 'Deleting…' : 'Yes'}
              </button>{' '}
              <button type="button" class="btn" disabled={del.busy} onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </span>
          ) : (
            <button type="button" class="btn" onClick={() => setConfirming(true)}>Delete</button>
          )}
        </div>
      </div>
      <p class="instructions-text">{idea.body}</p>
      {idea.reply ? (
        <div class="idea-reply" data-testid={`idea-${idea.id}-reply`}>
          <div class="empty">Reply{idea.reply_by ? ` — ${idea.reply_by}` : ''}</div>
          <p class="instructions-text">{idea.reply}</p>
        </div>
      ) : null}
      {idea.done_at ? (
        <div class="idea-done" data-testid={`idea-${idea.id}-done`}>
          <strong>Done</strong>
          {idea.done_note ? <p class="instructions-text">{idea.done_note}</p> : null}
        </div>
      ) : null}
      {canRespond ? <IdeaRespond idea={idea} /> : null}
      <IdeaAttachments idea={idea} />
      {del.error ? <small class="bad" role="alert">{del.error}</small> : null}
    </li>
  );
}

/** Owner/director: reply with a note, or mark done (note optional). */
function IdeaRespond({ idea }: { idea: AutomationIdea }): JSX.Element {
  const [mode, setMode] = useState<'idle' | 'reply' | 'done'>('idle');
  const [note, setNote] = useState('');
  const write = useWrite();

  async function send(kind: 'reply' | 'done'): Promise<void> {
    const path = kind === 'reply'
      ? `/api/v1/automation-ideas/${idea.id}/reply`
      : `/api/v1/automation-ideas/${idea.id}/done`;
    await write.run(async () => {
      await authedWrite('POST', path, { note: note.trim() });
      await reload();
    }, () => { setNote(''); setMode('idle'); });
  }

  if (mode === 'idle') {
    return (
      <div class="idea-respond" data-testid={`idea-${idea.id}-respond`}>
        <button type="button" class="btn" data-testid={`idea-${idea.id}-reply-open`}
          onClick={() => setMode('reply')}>
          Reply
        </button>{' '}
        {idea.done_at ? null : (
          <button type="button" class="btn" data-testid={`idea-${idea.id}-done-open`}
            onClick={() => void send('done')}>
            Completed
          </button>
        )}
      </div>
    );
  }

  const isReply = mode === 'reply';
  return (
    <div class="idea-respond" data-testid={`idea-${idea.id}-respond`}>
      <textarea
        class="notes-entry" rows={4}
        aria-label={isReply ? `Reply to idea ${idea.id}` : `Done note for idea ${idea.id}`}
        placeholder={isReply ? 'Write them back…' : 'A note if you need one (optional)'}
        value={note}
        onInput={(ev: Event) => setNote((ev.target as HTMLTextAreaElement).value)}
      />
      <button
        type="button" class="btn primary"
        data-testid={`idea-${idea.id}-${mode}-save`}
        disabled={write.busy || (isReply && !note.trim())}
        onClick={() => void send(mode)}
      >
        {write.busy ? 'Sending…' : (isReply ? 'Send reply' : 'Mark done')}
      </button>{' '}
      <button type="button" class="btn" disabled={write.busy} onClick={() => { setNote(''); setMode('idle'); }}>
        Cancel
      </button>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

/**
 * An idea's attached documents, and the row that adds one.
 *
 * A LINK, not an upload: this app stores no files, and Google already holds
 * them (the same rule task attachments follow). Anyone who can see the idea
 * can attach to it — "everyone" is the owner's own word, 2026-08-19.
 */
function IdeaAttachments({ idea }: { idea: AutomationIdea }): JSX.Element {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const add = useWrite();
  const remove = useWrite();
  const upload = useWrite();
  const files = idea.attachments ?? [];

  async function submit(): Promise<void> {
    const link = url.trim();
    if (!link) return;
    await add.run(async () => {
      await authedWrite('POST', `/api/v1/automation-ideas/${idea.id}/attachments`, {
        url: link, name: name.trim(),
      });
      await reload();
    }, () => { setUrl(''); setName(''); setAdding(false); });
  }

  /** Upload every file the picker hands back — one at a time, so a failure
   * names the file it happened on instead of failing the batch. */
  async function pick(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    await upload.run(async () => {
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        await uploadIdeaFile(idea.id, file);
      }
      await reload();
    }, () => { input.value = ''; });
  }

  async function drop(id: number): Promise<void> {
    await remove.run(async () => {
      await authedWrite('DELETE', `/api/v1/automation-idea-attachments/${id}`);
      await reload();
    });
  }

  return (
    <div data-testid={`idea-${idea.id}-attachments`}>
      {files.length ? (
        <ul class="attachment-list">
          {files.map((f) => (
            <li key={f.id} data-testid={`idea-attachment-${f.id}`}>
              <a class="doc-link" href={f.url} target="_blank" rel="noreferrer">
                {f.name || f.url}
              </a>{' '}
              <button
                type="button" class="btn" disabled={remove.busy}
                aria-label={`Remove ${f.name || f.url}`}
                onClick={() => void drop(f.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div class="attachment-add">
        {/* The FILE, stored — owner, 2026-08-19: "i need the ideas to store
            attachment since its being sent to me". A pasted link is somebody
            else's file in somebody else's folder; this keeps our own copy in
            the managers' shared drive. */}
        <label class="btn" data-testid={`idea-${idea.id}-upload-label`}>
          {upload.busy ? 'Uploading…' : 'Upload a document'}
          <input
            type="file" class="visually-hidden" aria-label="Upload a document"
            data-testid={`idea-${idea.id}-upload`}
            disabled={upload.busy}
            onChange={(ev: Event) => void pick(ev)}
          />
        </label>
      </div>

      {adding ? (
        <div class="attachment-add">
          <input
            type="url" aria-label="Document link" placeholder="Paste the link — Drive, Sheets, anything"
            value={url}
            onInput={(ev: Event) => setUrl((ev.target as HTMLInputElement).value)}
          />
          <input
            type="text" aria-label="Document name" placeholder="What it is (optional)"
            value={name}
            onInput={(ev: Event) => setName((ev.target as HTMLInputElement).value)}
          />
          <button
            type="button" class="btn primary" data-testid={`idea-${idea.id}-attach-save`}
            disabled={add.busy || !url.trim()}
            onClick={() => void submit()}
          >
            {add.busy ? 'Attaching…' : 'Attach'}
          </button>
          <button type="button" class="btn" disabled={add.busy} onClick={() => setAdding(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button" class="btn" data-testid={`idea-${idea.id}-attach`}
          onClick={() => setAdding(true)}
        >
          Attach a document
        </button>
      )}
      {add.error ? <small class="bad" role="alert">{add.error}</small> : null}
      {remove.error ? <small class="bad" role="alert">{remove.error}</small> : null}
      {upload.error ? <small class="bad" role="alert">{upload.error}</small> : null}
    </div>
  );
}

function isOwnerOrDirector(): boolean {
  const role = session.value.me?.role;
  return role === 'OWNER' || role === 'DIRECTOR';
}

export function IdeasPage(): JSX.Element {
  const clinics = visibleClinics();
  // Role, not "how many clinics did the last /api/me send". The owner has no
  // home clinic (location_code is null). Counting clinics used to hide the
  // picker and send an empty locationCode when he was signed in as himself.
  const isOwnerSide = isOwnerOrDirector() || clinics.length > 1;
  const [clinic, setClinic] = useState<LocationCode>(clinics[0] ?? LOCATION_ORDER[0]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [pending, setPending] = useState<File[]>([]);
  const [openCompletedId, setOpenCompletedId] = useState<number | null>(null);
  const add = useWrite();

  // N-078 (2026-09-08): "Build in the Ideas page a link for me to have all
  // the outstanding dev items" — the numbered Request Ledger, served
  // admin-only at GET /api/v1/dev/ledger (src/dev_ledger.js) as raw
  // text/html rather than the JSON-envelope the payroll archive uses, so
  // this fetches it with authedGetBlob (same helper Company.tsx uses for
  // NHP print) and reads the blob back as text rather than JSON.
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerHtml, setLedgerHtml] = useState<string | null>(null);
  const [ledgerStatus, setLedgerStatus] = useState<'idle' | 'loading' | 'ready' | 'fault'>('idle');
  const [ledgerError, setLedgerError] = useState<string | null>(null);

  async function toggleLedger(): Promise<void> {
    if (ledgerOpen) { setLedgerOpen(false); return; }
    setLedgerOpen(true);
    if (ledgerHtml !== null) return; // already loaded once this visit
    setLedgerStatus('loading');
    setLedgerError(null);
    try {
      const { blob } = await authedGetBlob('/api/v1/dev/ledger');
      setLedgerHtml(await blob.text());
      setLedgerStatus('ready');
    } catch (err) {
      setLedgerStatus('fault');
      setLedgerError(err instanceof ApiError ? err.message : 'could not load the ledger');
    }
  }

  const data = useResource<IdeasData>(KEY, fetchIdeas);
  const allIdeas = data.status === 'ready' ? (data.data.ideas ?? []) : [];
  const openIdeas = allIdeas.filter(isOpenIdea);
  const completedIdeas = allIdeas
    .filter((idea) => !isOpenIdea(idea))
    .slice()
    .sort((a, b) => new Date(completedOn(b)).getTime() - new Date(completedOn(a)).getTime());
  const openCompleted = completedIdeas.find((idea) => idea.id === openCompletedId) ?? null;
  useEffect(() => {
    void reload();
  }, []);

  function resolveClinic(): LocationCode {
    if (clinic && clinics.includes(clinic)) return clinic;
    return clinics[0] ?? LOCATION_ORDER[0];
  }

  async function submit(): Promise<void> {
    const leftover = [...pending];
    await add.run(async () => {
      if (!title.trim()) throw new Error('a title is required');
      if (!body.trim()) throw new Error('the note is required');
      const created = await authedWrite<{ idea: { id: number } }>(
        'POST', '/api/v1/automation-ideas', {
          locationCode: resolveClinic(),
          title: title.trim(),
          body: body.trim(),
          // A pasted link travels WITH the idea (owner, 2026-08-19), and is
          // omitted entirely when blank so an empty box is not a rejected
          // attachment.
          ...(fileUrl.trim()
            ? { attachments: [{ url: fileUrl.trim(), name: fileName.trim() }] }
            : {}),
        },
      );
      const ideaId = created?.idea?.id;
      if (!ideaId) throw new Error('the idea was not saved — try Send it again');
      // Attach now, before any reload. Waiting on IndexedDB first meant a
      // chosen file never left the machine if something later in the chain
      // was slow — and a failed attach used to skip the reload so the idea
      // looked unsaved. The file goes up against the new id; then we refresh.
      const failed: string[] = [];
      for (const file of leftover) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await uploadIdeaFile(ideaId, file);
        } catch (err) {
          failed.push(err instanceof Error ? err.message : String(err));
        }
      }
      await reload();
      if (failed.length) {
        throw new Error(`Idea saved. Could not attach: ${failed.join('; ')}`);
      }
    }, () => {
      setTitle(''); setBody(''); setFileUrl(''); setFileName(''); setPending([]);
    });
  }

  return (
    <div class="main" data-testid="ideas-page">
      <div class="page-header"><h1>Ideas to automate / bugs</h1></div>

      {session.value.me?.role === 'OWNER' ? (
        <div class="card">
          <button
            type="button"
            class="link"
            data-testid="dev-ledger-link"
            onClick={() => { void toggleLedger(); }}
          >
            {ledgerOpen ? 'Hide request ledger' : 'Request ledger (outstanding dev items)'}
          </button>
          {ledgerOpen && ledgerStatus === 'loading' ? <EmptyState label="Loading the ledger…" /> : null}
          {ledgerOpen && ledgerStatus === 'fault' ? (
            <FaultState name="the ledger did not load" detail={ledgerError ?? undefined} />
          ) : null}
          {ledgerOpen && ledgerStatus === 'ready' && ledgerHtml ? (
            // Owner-generated, server-stored content, not user input — same
            // sandbox choice as Payroll.tsx's archive frame: allow-scripts
            // alone renders blank (opaque origin), allow-same-origin is
            // needed for the ledger's own filter/status script to run.
            <iframe
              sandbox="allow-scripts allow-same-origin"
              srcDoc={ledgerHtml}
              title="Request ledger"
              data-testid="dev-ledger-frame"
              class="archive-frame"
              style={{ width: '100%', height: '80vh', border: 0 }}
            />
          ) : null}
        </div>
      ) : null}

      <div class="card">
        <div class="section-head">
          <h2>Add an idea or a bug</h2>
          <p>
            Something you do by hand every week that a computer could do instead,
            or something that is broken.
            {isOwnerSide ? '' : ' Tomer reads these.'}
          </p>
        </div>

        {/* An owner sees four clinics, so he must say which one; a manager has
            exactly one and is not asked a question with a single answer. */}
        {isOwnerSide ? (
          <label>
            Clinic{' '}
            <select
              aria-label="Clinic"
              value={clinic}
              onChange={(ev: Event) => setClinic((ev.target as HTMLSelectElement).value as LocationCode)}
            >
              {clinics.map((c) => (
                <option key={c} value={c}>{LOCATION_NAMES[c] ?? c}</option>
              ))}
            </select>
          </label>
        ) : null}

        <input
          type="text" class="idea-title" aria-label="Title"
          placeholder="One line — what it is"
          value={title}
          onInput={(ev: Event) => setTitle((ev.target as HTMLInputElement).value)}
        />
        <textarea
          class="notes-entry" rows={6} aria-label="The idea"
          placeholder="What you do now, how long it takes, and what you wish happened instead."
          value={body}
          onInput={(ev: Event) => setBody((ev.target as HTMLTextAreaElement).value)}
        />
        {/* Optional, and last, because the idea is the point and the document
            is the evidence. Two ways in on purpose: UPLOAD keeps our own copy
            in the managers' shared drive (owner, 2026-08-19: "i need the ideas
            to store attachment since its being sent to me"), and a pasted LINK
            still works for something already living in Drive. */}
        <div class="attachment-add">
          <label class="btn" data-testid="new-idea-upload-label">
            {pending.length
              ? `${pending.length} file${pending.length === 1 ? '' : 's'} will attach when you send`
              : 'Upload a document'}
            <input
              type="file" class="visually-hidden" multiple
              aria-label="Upload a document" data-testid="new-idea-upload"
              onChange={(ev: Event) => {
                const input = ev.target as HTMLInputElement;
                setPending(Array.from(input.files ?? []));
              }}
            />
          </label>
          <input
            type="url" aria-label="Document link"
            placeholder="…or paste a link (optional)"
            value={fileUrl}
            onInput={(ev: Event) => setFileUrl((ev.target as HTMLInputElement).value)}
          />
          <input
            type="text" aria-label="Document name"
            placeholder="What it is (optional)"
            value={fileName}
            onInput={(ev: Event) => setFileName((ev.target as HTMLInputElement).value)}
          />
        </div>

        <button
          type="button" class="btn primary" data-testid="add-idea"
          disabled={add.busy}
          onClick={() => void submit()}
        >
          {add.busy ? 'Saving…' : 'Send it'}
        </button>
        {pending.length && (!title.trim() || !body.trim()) ? (
          <small class="empty" data-testid="pending-hint">
            File chosen — add a title and the note, then press Send it. The file does not upload on its own.
          </small>
        ) : null}
        {add.error ? <small class="bad" role="alert">{add.error}</small> : null}
      </div>

      <div class="card">
        <div class="section-head">
          <h2>{isOwnerSide ? 'Everything the managers have sent' : 'What this clinic has sent'}</h2>
        </div>

        {data.status === 'loading' ? <EmptyState label="Loading ideas…" /> : null}
        {data.status === 'fault'
          ? <FaultState name="could not load the ideas" detail={data.error} />
          : null}

        {data.status === 'ready' ? (
          openIdeas.length === 0 ? (
            <EmptyState label="Nothing open." />
          ) : (
            <ul class="notes-list" data-testid="idea-list">
              {openIdeas.map((idea) => (
                <li key={idea.id} class="idea-wrap">
                  {isOwnerSide ? (
                    <div class="empty">{LOCATION_NAMES[idea.location_code as LocationCode] ?? idea.location_code}</div>
                  ) : null}
                  <ul class="notes-list"><IdeaRow idea={idea} /></ul>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>

      <div class="card">
        <div class="section-head">
          <h2>Completed</h2>
        </div>
        {completedIdeas.length === 0 ? (
          <EmptyState label="Nothing completed yet." />
        ) : (
          <div class="table-wrap">
            <table class="data" data-testid="idea-completed-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Idea</th>
                  <th>Date done</th>
                </tr>
              </thead>
              <tbody>
                {completedIdeas.map((idea) => (
                  <tr
                    key={idea.id}
                    data-testid={`idea-completed-${idea.id}`}
                    class={openCompletedId === idea.id ? 'on' : undefined}
                    onClick={() => setOpenCompletedId((cur) => (cur === idea.id ? null : idea.id))}
                  >
                    <td>{LOCATION_NAMES[idea.location_code as LocationCode] ?? idea.location_code}</td>
                    <td>{idea.title}</td>
                    <td>{formatDate(clinicNow(new Date(completedOn(idea))))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {openCompleted ? (
          <div data-testid={`idea-completed-open-${openCompleted.id}`}>
            <ul class="notes-list"><IdeaRow idea={openCompleted} /></ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Exported for the nav test: the page exists and is reachable. */
export const IDEAS_PATH = '/ideas';

export function canSeeIdeas(): boolean {
  return session.value.me?.mapped === true;
}
